import React, { useMemo } from 'react';
import { Alert, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  applyGroupDevicesSelection,
  getDeviceKey,
  groupDevicesByCurrentGroup
} from '@modules/digital-signage/utils/digital-signage.utils.ts';
import { MoveToGroupAlertProps } from '@modules/digital-signage/model/devices.model.ts';
import DevicesChipsAutocomplete from '../devices-chips-autocomplete/devices-chips-autocomplete.tsx';

const MoveToGroupAlert: React.FC<MoveToGroupAlertProps> = ({
  sourceDevices,
  selectedDevices,
  onChange
}) => {
  const { t } = useTranslation();
  const groupedDevices = useMemo(
    () => groupDevicesByCurrentGroup(sourceDevices),
    [sourceDevices]
  );

  const selectedKeys = useMemo(
    () => new Set(selectedDevices.map(getDeviceKey)),
    [selectedDevices]
  );

  if (!groupedDevices.length) return null;

  return (
    <Alert
      severity="warning"
      className="items-start! [&_.MuiAlert-message]:w-full"
    >
      <Typography variant="body2" className="font-semibold!">
        {t('move_to_group_info_title')}
      </Typography>
      <Typography
        variant="caption"
        component="p"
        className="mt-1 leading-5! text-(--dark-alpha-800)!"
      >
        {t('move_to_group_info_text')}
      </Typography>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-3 items-center mt-3 px-3 py-2.5 rounded-lg bg-(--white) border border-(--orange-2)">
        {groupedDevices.map((group) => (
          <React.Fragment key={group.groupId}>
            <Typography
              variant="caption"
              className="max-w-40 px-2.5 py-1 rounded-full bg-(--gray-10) text-(--dark-alpha-800)! break-words"
            >
              {group.groupName}
            </Typography>
            <div className="min-w-0">
              <DevicesChipsAutocomplete
                value={group.devices.filter((device) =>
                  selectedKeys.has(getDeviceKey(device))
                )}
                options={group.devices}
                onChange={(nextGroupDevices) =>
                  onChange(
                    applyGroupDevicesSelection(
                      sourceDevices,
                      selectedDevices,
                      group.devices,
                      nextGroupDevices
                    )
                  )
                }
                placeholder={t('move_to_group_devices_placeholder')}
              />
            </div>
          </React.Fragment>
        ))}
      </div>
    </Alert>
  );
};

export default MoveToGroupAlert;
